import { Action, createReducer, on } from '@ngrx/store';
import { PICK } from 'app/_components/back-counter-request-tab/back-counter-request-tab.utils';
import * as actions from './back-counter.actions';
import { BackCounterConfig } from './back-counter.config';
import { BackCounterRequestDetails, BackCounterRequestPartItemAction } from './back-counter.entities';
import { getUnsavedChanges, patchRequestPartItems } from './back-counter.reducer.util';
import { BackCounterState } from './back-counter.state';

export const initialState: BackCounterState = {
  requests: [],
  openRequests: [],
  openRequestsAttachments: [],
  loading: false,
  totalRequestCount: 0,
  requestDetailsLoading: false,
  unsavedChanges: {},
  isConfirmationLoading: false,
  priceVerifyItems: {},
  partAddedToRequest: null,
  requestNotes: [],
  openedHeaderNotes: {},
  inventories: [],
  partBinLocationCollection: null,
  readyToPickData: null,
  lastCancelledOrderNumber: null
};

export const backCounterReducer = createReducer<BackCounterState, Action>(
  initialState,
  on(actions.getBackCounterRequests, (state: BackCounterState) => ({
    ...state,
    loading: true
  })),
  on(actions.getBackCounterRequestsSuccess, (state: BackCounterState, { requests }) => ({
    ...state,
    requests: requests.summaryList,
    totalRequestCount: requests.totalCount,
    loading: false
  })),
  on(actions.getBackCounterRequestsFailed, (state: BackCounterState) => ({
    ...state,
    loading: false
  })),
  on(actions.closeBackCounterRequest, (state: BackCounterState, { requestId }) => {
    const currentUnsavedChanges = {...state.unsavedChanges};
    currentUnsavedChanges[requestId] = {itemsToAdd: [], itemsToRemove: [], itemsToUpdate: []};
    return ({
    ...state,
    selectedRequest: null, openRequests: state.openRequests.filter(x => x.orderNumber !== requestId),
    unsavedChanges: currentUnsavedChanges,
  })}),
  on(
    actions.openBackCounterRequest,
    (state: BackCounterState, { requestDetails }) => {
      let openRequests: BackCounterRequestDetails[] = [];
      if (
        !state.openRequests.some(
          (r) => r.orderNumber === requestDetails.orderNumber
        ) &&
        state.openRequests.length < BackCounterConfig.activeRequestsLimit
      ) {
        openRequests = [...state.openRequests, requestDetails];
      } else {
        openRequests = state.openRequests;
      }
      return {
        ...state,
        openRequests,
        requestDetailsLoading: true,
      };
    }
  ),
  on(actions.editBackCounterRequest, (state: BackCounterState) => ({
    ...state,
    requestDetailsLoading: true,
  })),
  on(actions.getRequestDetailsSuccess, (state: BackCounterState, { requestDetails }) => {
    const normalizedRequestDetails = {
      ...requestDetails,
      orderNumber: requestDetails.requestType === PICK ? requestDetails.pickreqno : requestDetails.orderNumber
    }
    const openRequests: BackCounterRequestDetails[] = [...state.openRequests];
    const requestIndex = openRequests.findIndex((r) => r.orderNumber === normalizedRequestDetails.orderNumber);
    const attachmentsLoaded = openRequests[requestIndex]?.photoExists &&
    openRequests[requestIndex].attachments?.length > 0 &&
    openRequests[requestIndex].orderNumber === normalizedRequestDetails.orderNumber;
    const attachments = attachmentsLoaded ? openRequests[requestIndex].attachments : normalizedRequestDetails.attachments;
    const ordertogetitem = normalizedRequestDetails.ordertogetitem?.map(m => ({
      ...m,
      couponPerQuantity: Math.abs(m?.coupTotal ?? 0) / (m?.orderQuantity ?? 1),
      coreChargesPerQuantity: (m?.coreCharges && m?.coreCharges > 0) ? m?.coreCharges / (m?.orderQuantity ?? 1) : 0,
      itemTotal: (m?.unitPrice ?? 0) * (m?.orderQuantity ?? 0),
      vendor: m.vendor ?? "" + m.purchaseVendor
    }));
    openRequests[requestIndex] = {
      ...normalizedRequestDetails,
      ordertogetitem,
      attachments
    };
    return ({
      ...state,
      openRequests,
      requestDetailsLoading: false
    });
  }),
  on(actions.getRequestDetailsFailed, (state: BackCounterState) => ({
    ...state,
    requestDetailsLoading: false
  })),
  on(actions.getRequestAttachmentsSuccess, (state: BackCounterState, { attachments, orderNumber }) => {
    const idx = state.openRequestsAttachments.findIndex(f => f.orderNumber === orderNumber);
    let openRequestsAttachments = [...state.openRequestsAttachments];
    if(idx >= 0) {
      openRequestsAttachments = openRequestsAttachments.map(m => {
        if(m.orderNumber === orderNumber) {
          return {
            orderNumber,
            attachments
          }
        } else {
          return {
            orderNumber: m.orderNumber,
            attachments: m.attachments
          }
        }
      })
    } else {
      openRequestsAttachments.push({
        orderNumber,
        attachments
      })
    }
    return ({...state,
      openRequests: state.openRequests.map(
        item => item.orderNumber === orderNumber ? {
          ...item,
          attachments:[...attachments],
      } : item ),
      openRequestsAttachments
    })
  }),
  on(
    actions.syncUnsavedChanges, (state: BackCounterState, { orderNumber, editedParts, part, esn, crc, vin }) => {
    const parts = state.openRequests.find(r => r.orderNumber === orderNumber)?.ordertogetitem;
    // Pass partAddedToRequest to patchRequestPartItems for ALL mutations
    const patchedParts = patchRequestPartItems([...parts], part, editedParts, state.partAddedToRequest);
    const resp = {
      ...state,
      partAddedToRequest: null,
      openRequests: state.openRequests.map(
        item => {
          if(item.orderNumber === orderNumber){
            return {...item, ordertogetitem: patchedParts, esn: esn ?? item.esn, crc: crc ?? item.crc, vin: vin ?? item.vin, hasEngineSerialNumberUpdated: !!esn };
          } else {
            return item
          }
        }),
      unsavedChanges: {
        ...state.unsavedChanges,
        // Now getUnsavedChanges is pure - only categorizes
        [orderNumber]: getUnsavedChanges(patchedParts)
      },
    };
    return resp;
  }),
  on(actions.updateBackCounterRequest, (state: BackCounterState) => ({
    ...state,
    requestDetailsLoading: true
  })),
  on(actions.updateBackCounterRequestSuccess, (state: BackCounterState, {requestId}) => {
    const currentUnsavedChanges = {...state.unsavedChanges};
    currentUnsavedChanges[requestId] = {itemsToAdd: [], itemsToRemove: [], itemsToUpdate: []};
    return ({
      ...state,
      priceVerifyItems: {},
      unsavedChanges: {...initialState.unsavedChanges},
      requestDetailsLoading: false
    });
  }),
  on(actions.updateBackCounterRequestFailed, (state: BackCounterState) => ({
    ...state,
    requestDetailsLoading: false
  })),
  on(actions.completeConfirmed, (state: BackCounterState, { lockConfirm }) => ({
    ...state,
    isConfirmationLoading: lockConfirm,
    requestDetailsLoading: lockConfirm
  })),
  on(actions.changeLoading, (state: BackCounterState, { loading }) => ( {
    ...state,
    loading
  })),
  on(actions.BCTPriceVerifySuccess, (state, {priceVerifyParts, requestId}) => {
    const verifiedItemsForRequest = priceVerifyParts.priceVerifyItems.reduce((prev, curr) => {
      if (curr.isPriceVerified === true) {
        prev[curr.partNumber] = curr;
      }
      return prev;
    }, {});
    
    const priceVerifyItems = {...state.priceVerifyItems};
    // Merge new verification results with existing ones instead of replacing completely
    priceVerifyItems[requestId] = {
      ...priceVerifyItems[requestId],
      ...verifiedItemsForRequest
    };

    const currentRequest = {
      ...state.openRequests.find((x) => x.orderNumber === requestId),
    };
    const remainingOpenRequests = state.openRequests.filter(
      (x) => x.orderNumber !== requestId,
    );
    if(currentRequest){
      currentRequest.ordertogetitem = currentRequest.ordertogetitem.map(x => {
        const priceVerifiedItem = priceVerifyParts.priceVerifyItems.find(y => y.partNumber === x.materialNumber);
        // Only update isPriceVerified if this part was included in the current verification
        if (priceVerifiedItem) {
          const couponReduction =
            priceVerifiedItem.coupons?.reduce(
              (sum: number, coupon: any) => sum + (coupon.amount ?? 0),
              0,
            ) ?? 0;
          const reducedPrice = priceVerifiedItem.unitPrice - couponReduction;
          return {
            ...x,
            isPriceVerified: priceVerifiedItem.isPriceVerified,
            unitPrice: priceVerifiedItem.unitPrice,
            couponPerQuantity: couponReduction,
            itemTotal: priceVerifiedItem.quantity * reducedPrice,
          };
        }
        // Preserve existing isPriceVerified status for parts not included in current verification
        return x;
      });
    }

    const updatedUnsavedChanges = {
      ...state.unsavedChanges,
      [requestId]: currentRequest?.ordertogetitem
        ? getUnsavedChanges(currentRequest.ordertogetitem)
        : state.unsavedChanges[requestId]
    };

    return {
      ...state,
      priceVerifyItems,
      openRequests: [...remainingOpenRequests, currentRequest],
      unsavedChanges: updatedUnsavedChanges,
    };
  }),
  on(actions.partAddedToRequest, (state, {part}) => {
    return ({...state, partAddedToRequest: part});
  }),
  on(actions.openNotesModal, (state, { config }) => {
    const openRequests = JSON.parse(JSON.stringify([...state.openRequests]));
    const openRequest = openRequests.find(f => f.orderNumber === config.id);
    const item = openRequest?.ordertogetjob?.find(f => f.jobNumber === config.pos);
    if(item) {
      item.jobNotifflag = "";
    }

    // mapping deliveryDate field
    const unsavedChanges = JSON.parse(JSON.stringify(state.unsavedChanges));
    const order = unsavedChanges[config.id];
    const itemsToAdd = order?.itemsToAdd ?? [];
    const deliveryDate = itemsToAdd?.find(f => f?.deliveryDate)?.deliveryDate;
    const itemLast = openRequest?.ordertogetitem[openRequest?.ordertogetitem?.length - 1];
    if(itemLast) {
      itemLast.deliveryDate = deliveryDate;
    }
    return ({...state, openRequests });
  }),
  on(actions.closeNotesModal, (state, { config }) => {
    let openedHeaderNotes = {};
    if(config.notePosition === 'header') {
      openedHeaderNotes[config.id] = true;
    }
    return ({...state, openedHeaderNotes });
  }),
  on(actions.attachmentSent, (state, {orderNumber}) => {
    const resp = {
      ...state,
      unsavedChanges: {
        ...state.unsavedChanges,
        [orderNumber]: {...state.unsavedChanges[orderNumber], hasUnsavedAttachment: true}
      },
      requestDetailsLoading: true
    };
    return resp;
  }),
  on(actions.attachmentSentSuccess,
    actions.attachmentSentFailed,
    actions.getRequestAttachments, (state: BackCounterState) => ({
    ...state,
    requestDetailsLoading: false,
  })),
  on(actions.setLoading, (state: BackCounterState, {requestDetailsLoading}) => ({
    ...state,
    requestDetailsLoading,
  })),
  on(actions.getRequestNotesSuccess, (state: BackCounterState, {requestNotes}) => ({
    ...state,
    requestNotes,
  })),
  on(actions.setUnitPrice, (state: BackCounterState, {orderNumber, materialNumber, price}) => ({
    ...state,
    openRequests: state.openRequests.map(m => {
      if(m.orderNumber === orderNumber) {
        return {
          ...m,
          ordertogetitem: [...m.ordertogetitem.map(n => {
            if(n.materialNumber === materialNumber) {
              return {
                ...n,
                manualPrice: price,
                unitPrice: price
              }
            }
            return n;
          })]
        }
      }
      return m;
    })
  })),
  on(actions.getInventorySuccess, (state, { inventories }) =>
    ({
      ...state,
      inventories
    })
  ),
  on(actions.readyToPickSuccess, (state, { hasSuccessMessage, orderNumber }) => {
    return ({
      ...state,
      requestDetailsLoading: false,
      openRequests: state.openRequests.map(m => {
        if(m.orderNumber === orderNumber) {
          return {
            ...m,
            rtpButton: hasSuccessMessage ? null : "X"
          }
        }
        return m;
      })
    })
  }),
  on(actions.readyToPickFailed, (state) => ({
    ...state,
    requestDetailsLoading: false
  })),
  on(
    actions.readyToPickButtonPressed,
    (state, { branchCode, items, sapOrderNo }) => ({
      ...state,
      requestDetailsLoading: true,
      readyToPickData: {
        branchCode,
        items,
        sapOrderNo,
        openingFrom: "BCT",
      },
    })
  ),
  on(
    actions.getPartBinLocationsSuccess,
    (state, { partBinLocationCollection }) => ({
      ...state,
      partBinLocationCollection,
      readyToPickData: null,
    })
  ),
  on(actions.cancelRequestFailed, (state) => ({
    ...state,
    requestDetailsLoading: false,
    loading: false,
  })),
  on(actions.cancelRequestSuccess, (state, { orderNumber }) => ({
    ...state,
    requestDetailsLoading: false,
    loading: false,
    lastCancelledOrderNumber: orderNumber,
    openRequests: state.openRequests.map((m) => {
      if (m.orderNumber === orderNumber) {
        return {
          ...m,
          prStatus: "8",
        };
      }
      return m;
    }),
  })),
  on(actions.completeConfirmed, (state, {orderNumber}) => ({
    ...state,
    openRequests: state.openRequests.map(m => {
      if(orderNumber && m.orderNumber === orderNumber) {
        return {
          ...m,
          isLocked: false
        }
      }
      return m;
    }),
  }))
);
